// ============================================================================
// L'ONGLET « HISTORIQUE » de la fiche article — relevé maquette l.4163-4207.
//
// ⚠️ « Chaque modification est un événement. Rien ne s'écrase en silence. » — la promesse est celle de
// la maquette, et elle tient PARCE QUE le substrat est event-sourced : ce que l'écran montre est le
// journal lui-même, pas une table d'audit qu'un producteur aurait pu oublier de remplir.
//
// ⚠️ L'ÂGE DU JOURNAL EST DIT. La rétention purge les vieux événements, et un magasin repris n'a qu'un
// seul événement d'amorçage par article : un historique court n'est donc pas une anomalie. Le taire
// laisserait croire que rien n'a jamais bougé.
// ============================================================================
import { useEffect, useState } from 'react';
import { Copyable } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';
import { api, type ArticleHistory, type ArticleChange } from '../api';
import { key, t, type Translatable } from '../i18n';
import { ARTICLE_FLAG_LABELS, ITEM_KIND_TEXT, articleFlagReads } from '../article-flags';
import { isItemKind } from '@orgazone/core-catalog';
import { fieldLabel } from '../vocabulary';

/**
 * Les champs suivis dont le mot vient du VOCABULAIRE du magasin — nom du champ dans le journal → clé
 * du catalogue. Le journal nomme ses champs par la colonne de `article`, et `family` n'y est pas
 * `family_id` : la correspondance est donc dite ici, pas dérivée du catalogue.
 * ⚠️ DES CLÉS, JAMAIS DES MOTS : le mot se demande AU RENDU (`fieldLabel`). Une table de mots au niveau
 *    du module figerait « Saison » avant que le magasin ne soit lu — un caviste lirait « Saison » à vie.
 */
const VOCABULARY_KEY_BY_FIELD: Record<string, VocabularyFieldKey> = {
  name: 'name', gencode: 'gencode', article_code: 'articleCode', model_code: 'modelCode',
  sale_price_incl_tax_cents: 'salePriceInclTax', purchase_price_excl_tax_fine: 'purchasePriceExclTax',
  vat_rate_bp: 'vatRate', purchase_regime: 'purchaseRegime',
  family: 'family', sector: 'sector', supplier: 'supplier', season: 'season', brand: 'brand',
  color: 'color', size: 'size', size_grid_id: 'sizeGrid',
  weight_g: 'weight', origin_country: 'originCountry', customs_code: 'customsCode', composition: 'composition',
};

/**
 * Les champs suivis qui ne sont PAS du vocabulaire : le geste de création et les drapeaux, dont le mot
 * est une PHRASE à polarité, identique dans tous les magasins.
 */
const FIXED_LABELS: Record<string, Translatable> = {
  __created__: key('Création'),
  item_kind: key('Nature de l’article'),
  is_active: key('Actif'), is_web_active: key('Visible sur le web'), is_discount_active: key('Remises autorisées'),
  // ⚠️ LES DEUX DRAPEAUX D'EXCLUSION VIENNENT DU DICTIONNAIRE PARTAGÉ, pas d'ici. Recopiés, ils
  //    avaient déjà divergé : cet écran disait « Géré en stock » quand la fiche disait « Non géré
  //    en stock » — la même action, décrite à l'envers, deux écrans plus loin.
  ...Object.fromEntries(Object.entries(ARTICLE_FLAG_LABELS).map(([col, f]) => [col, f.label])),
};

/**
 * Le nom HUMAIN d'un champ suivi, tel que CE magasin le dit.
 * ⚠️ Un champ que ni le vocabulaire ni les phrases fixes ne connaissent s'affiche sous son nom technique
 * plutôt que d'être MASQUÉ : une modification qu'on ne sait pas nommer reste une modification, et la
 * cacher ferait mentir la promesse « rien ne s'écrase en silence ».
 */
export function historyFieldLabel(field: string): string {
  const key = VOCABULARY_KEY_BY_FIELD[field];
  if (key) return fieldLabel(key);
  const fixed = FIXED_LABELS[field];
  return fixed ? t(fixed) : field;
}

/** Les champs dont la valeur brute ne se lit pas — un entier de centimes n'est pas un prix. */
const MONTANTS = new Set(['sale_price_incl_tax_cents']);
const BOOLEENS = new Set(['is_active', 'is_web_active', 'is_discount_active', 'excluded_from_stats', 'is_stock_managed']);

/** Rend une valeur telle qu'un humain la reconnaît — jamais l'entier brut de la base. */
function valeur(champ: string, v: string | null): string {
  if (v === null) return '—';
  if (MONTANTS.has(champ)) return formatMoney(Number(v) || 0, { thousandsSep: ' ' });
  if (champ === 'purchase_price_excl_tax_fine') return formatMoney(Math.round((Number(v) || 0) / 100), { thousandsSep: ' ' });
  if (champ === 'vat_rate_bp') return `${((Number(v) || 0) / 100).toFixed(2).replace('.', ',')} %`;
  if (champ === 'weight_g') return `${v} g`;
  if (champ === 'purchase_regime') return v === 'corner' ? t('Corner') : t('Achat ferme');
  if (champ === 'item_kind') return isItemKind(v) ? t(ITEM_KIND_TEXT[v].label) : v;
  // ⚠️ LA VALEUR SUIT LA PHRASE, pas la colonne : « Hors suivi de stock » sous une valeur brute
  //    `is_stock_managed = 1` afficherait « oui » pour un article qui EST suivi — le contraire
  //    de ce que la ligne affirme. L'inversion est déclarée une fois, dans `article-flags`.
  if (BOOLEENS.has(champ)) {
    const brut = v === '1' || v === 'true';
    return articleFlagReads(champ, brut) ? 'oui' : 'non';
  }
  return v;
}

const quand = (ms: number): string =>
  new Date(ms).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });

/** Un jour seul — pour une date d'origine, l'heure d'un système qu'on a quitté n'apprend rien. */
const theDay = (ms: number): string =>
  new Date(ms).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * QUI a écrit — en français, jamais le jeton technique.
 * ⚠️ L'écran rendait `seed` et `bo:max@orgazone.fr` tels quels : deux noms internes affichés comme des
 * auteurs. « seed » ne veut rien dire pour qui lit un historique, et c'est pourtant l'auteur de TOUTES
 * les lignes d'un magasin repris — donc le mot le plus lu de cet écran.
 */
function acteurLisible(actor: string | null, nom?: string | null): string {
  if (!actor) return 'auteur inconnu';
  if (actor === 'seed') return 'reprise';
  if (actor === 'system') return 'automatique';
  // ⚠️ LE NOM QUAND ON L'A, L'ADRESSE SINON (demande utilisateur 2026-08-25 : « je ne veux pas
  // l'adresse mais Prénom Nom »). Il est résolu au serveur depuis l'annuaire des comptes — le journal,
  // lui, garde l'adresse : un nom change, un journal append-only ne se réécrit pas. Un compte
  // supprimé garde donc son adresse à l'écran, ce qui reste plus utile qu'un tiret.
  if (nom?.trim()) return nom.trim();
  return actor.startsWith('bo:') ? actor.slice(3) : actor;
}

export function HistoriqueArticle({ modelKey }: { modelKey: string }) {
  const [h, setH] = useState<ArticleHistory | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.articleHistory(modelKey)
      .then((r) => { if (vivant) { setH(r); setFailure(null); } })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey]);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Historique complet')}</div>
      <p style={{ margin: '3px 0 14px', fontSize: '12.5px', color: 'var(--ink-500)' }}>
        
        {t('Chaque modification est un événement. Rien ne s’écrase en silence.')}
        {h?.since ? <> {t('Journal disponible depuis le {v}.', { v: quand(h.since) })}</> : ''}
      </p>

      {failure !== null
        ? <p role="alert" style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
            
            {t('L’historique n’a pas pu être lu : {failure}', { failure })}
          </p>
        : h === null
          ? <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Lecture du journal…')}</p>
          : h.rows.length === 0 ? <Vide /> : <Liste h={h} />}
    </div>
  );
}

function Liste({ h }: { h: ArticleHistory }) {
  return (
    <>
      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        {h.rows.map((e) => <Row key={e.id} e={e} />)}
      </div>
      {/* ⚠️ La troncature SE DIT : sans elle, « il ne s'est rien passé avant » et « je n'ai pas tout
          lu » se ressemblent trait pour trait. */}
      {h.truncated && (
        <p style={{ margin: '10px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
          
          {t('Seuls les événements les plus récents sont affichés — le journal en contient davantage.')}
        </p>
      )}
    </>
  );
}

function Row({ e }: { e: ArticleChange }) {
  const creation = e.field === '__created__';
  // ⚠️ UNE REPRISE N'EST PAS UNE CRÉATION, et le mot compte. `at` dit quand CETTE base a appris
  // l'article — pas quand il est né. Sur la base de bêta, tous les articles portaient « créé le 17
  // août 2026 » pour des dates réelles allant de 2015 à juillet 2026 : la ligne affirmait une date
  // fausse avec l'aplomb d'une donnée journalisée (§23.0). On nomme donc le geste pour ce qu'il est,
  // et on rend les DEUX dates — celle d'origine et celle de la reprise.
  const reprise = creation && e.origin != null;
  return (
    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--ink-100)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>
          {reprise ? t('Repris du système précédent') : historyFieldLabel(e.field)}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{quand(e.at)}</span>
        {/* Le gencode DIT quelle déclinaison a bougé : un modèle en compte douze, et « prix modifié »
            sans préciser laquelle n'apprend rien. ⚠️ MAIS DÈS QU'ELLES SONT PLUSIEURS, c'est le COMPTE
            qui informe : en désigner une au hasard serait pire que n'en désigner aucune. */}
        {e.variants > 1 ? (
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>
            {t('{n} déclinaison', { n: e.variants })}
          </span>
        ) : e.gencode ? (
          <Copyable value={e.gencode} label={t('Copier le code-barres')}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{e.gencode}</span>
          </Copyable>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 5, flexWrap: 'wrap' }}>
        {creation ? (
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
            {e.to ?? t('Déclinaison créée')}
            {reprise && (
              <span style={{ fontWeight: 400, color: 'var(--ink-500)' }}>
                {t('— créé le {v} dans le système d’origine', { v: theDay(e.origin!) })}
              </span>
            )}
          </span>
        ) : (
          <>
            <span style={{ fontSize: '12.5px', color: 'var(--ink-500)', textDecoration: 'line-through' }}>
              {valeur(e.field, e.from)}
            </span>
            <span aria-hidden style={{ color: 'var(--ink-400)', display: 'flex' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </span>
            <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
              {valeur(e.field, e.to)}
            </span>
          </>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{acteurLisible(e.actor, e.actorName)}</span>
      </div>
    </div>
  );
}

/**
 * ⚠️ IL DIT POURQUOI C'EST VIDE, pas seulement que ça l'est. Un journal sans événement sur cet article
 * veut dire « rien n'a été modifié depuis sa mise en service », pas « l'historique est en panne ».
 */
function Vide() {
  return (
    <div style={{
      padding: '30px 20px', textAlign: 'center',
      border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-sm)',
    }}>
      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune modification enregistrée')}</div>
      <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.5 }}>
        
        {t('Cet article n’a pas changé depuis que le journal le suit.')}
      </div>
    </div>
  );
}
